import React from "react";
import PropTypes from "prop-types";
import { TableRow, TableCell, Checkbox, TableSortLabel } from "@mui/material";
import { COLOR_CODE } from "@/utils/constants/colors";

const DataTableHead = ({
  columns,
  sortConfig,
  handleSort,
  isSelectAll,
  records,
  selectedRows,
  handleSelectAll,
  partialSelectAll,
  deselectedItems,
  disabledHeaders = [],
  hideSelectAll = false,
  hideCheckbox = false,
  initialSelectedCount = 0,
  totalRecords = 0,
  disableSelectionPersistence = false
}) => {
// Common helper to get row ID
const getRowId = row => row.rewardId ?? row.id ?? row.uuid ?? row.productId ?? row.storeId;

const handleDisable = (column) => {
  return disabledHeaders.includes(column.label_of_column);
}

  return (
    <TableRow>
      {/* If both hideSelectAll and hideCheckbox are true, hide the entire cell */}
      {!(hideSelectAll && hideCheckbox) && (
        <TableCell sx={{
          width: '60px',
        }}>
          {/* If only hideCheckbox is true (but hideSelectAll is false), show cell but hide checkbox */}
          {!hideSelectAll && !hideCheckbox && (
            <Checkbox
              size="small"
              className="custom-checkbox"
              checked={
                isSelectAll ||
                (partialSelectAll && deselectedItems?.size === 0) ||
                (records?.length > 0 && records.every(record => {
                  const recordId = getRowId(record);
                  // If in partial select all mode, check if item is NOT in deselected items
                  if (partialSelectAll) {
                    return !deselectedItems?.has(recordId);
                  }
                  // Otherwise check if item is in selected rows
                  return selectedRows.some(selected => getRowId(selected) === recordId);
                }))
              }
              onChange={handleSelectAll}
              indeterminate={
                (partialSelectAll && deselectedItems?.size > 0) ||
                (!isSelectAll && !partialSelectAll && (
                  (!disableSelectionPersistence && initialSelectedCount > 0 && initialSelectedCount < totalRecords) ||
                  (selectedRows?.length > 0 && selectedRows?.length < records.length)
                ))
              }
            />
          )}
        </TableCell>
      )}
    {columns.map((column, columnIndex) => {
      const isActive = sortConfig?.key === column.name_of_db_column;
      const sortDirection = isActive ? (sortConfig?.direction || 'asc') : 'asc';
      const shouldHideSortIcon = column?.hideSortIcon
        ? true
        : !isActive;

      const effectiveMinWidth = column?.minWidth
        ? column.minWidth
        :  210;

      return (
        <TableCell
          className="custom-data-table-cell"
          key={`header-${columnIndex + 1}`}
          align="left"
          sx={effectiveMinWidth ? { minWidth: effectiveMinWidth } : undefined}
        >
          <TableSortLabel
            active={isActive}
            disabled={handleDisable(column)}
            direction={sortDirection}
            onClick={() => {
              if (!column?.hideSortIcon) {
                handleSort(column.name_of_db_column);
              }
            }}
            hideSortIcon={shouldHideSortIcon}
            sx={{
              color: `${COLOR_CODE._EFEFEF} !important`,
              '&.Mui-active': {
                color: `${COLOR_CODE._EFEFEF} !important`,
              },
              '&:hover': {
                color: `${COLOR_CODE._EFEFEF} !important`,
              },
              '& .MuiTableSortLabel-icon': {
                color: `${COLOR_CODE._EFEFEF} !important`,
              },
            }}
          >
            {column?.header_of_column || column.label_of_column}
          </TableSortLabel>
        </TableCell>
      );
    })}
    {/* If both hideSelectAll and hideCheckbox are true, hide the trailing cell */}
    {!(hideSelectAll && hideCheckbox) && (
      <TableCell sx={{
        width: '60px',
      }}>
      </TableCell>
    )}
  </TableRow>
  );
};

DataTableHead.propTypes = {
  columns: PropTypes.array.isRequired,
  sortConfig: PropTypes.object,
  handleSort: PropTypes.func.isRequired,
  isSelectAll: PropTypes.bool.isRequired,
  records: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  handleSelectAll: PropTypes.func.isRequired,
  partialSelectAll: PropTypes.bool,
  deselectedItems: PropTypes.instanceOf(Set),
  hideSelectAll: PropTypes.bool,
  hideCheckbox: PropTypes.bool,
  initialSelectedCount: PropTypes.number,
  totalRecords: PropTypes.number,
  disableSelectionPersistence: PropTypes.bool
};

export default DataTableHead;
